import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LifeBuoy, Loader2, Mail, MailOpen, Trash2, Building2, Reply, Clock, Tag } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

export default function SupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { setTickets((await api.get("/admin/support-tickets")).data.tickets); }
    catch { toast.error("Failed to load tickets"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleRead = async (t) => {
    const next = !t.read;
    setBusy(t.id);
    try {
      await api.post(`/admin/support-tickets/${t.id}/read`, { read: next });
      setTickets((list) => list.map((x) => (x.id === t.id ? { ...x, read: next } : x)));
    } catch { toast.error("Couldn't update ticket"); }
    finally { setBusy(null); }
  };

  const remove = async (t) => {
    setBusy(t.id);
    try {
      await api.delete(`/admin/support-tickets/${t.id}`);
      setTickets((list) => list.filter((x) => x.id !== t.id));
      toast.success("Ticket deleted");
    } catch { toast.error("Delete failed"); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="flex items-center justify-center py-40"><Loader2 className="h-6 w-6 text-amber-400 animate-spin" /></div>;

  const unread = tickets.filter((t) => !t.read).length;
  const filtered = tickets.filter((t) => filter === "all" || (filter === "unread" ? !t.read : t.read));

  return (
    <div className="p-6 lg:p-8 max-w-[1000px] mx-auto" data-testid="support-tickets-page">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3">
          <LifeBuoy className="h-7 w-7 text-amber-400" /> Support Tickets
          {unread > 0 && <span data-testid="tickets-unread-badge" className="text-sm font-semibold px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">{unread} unread</span>}
        </h1>
        <p className="text-gray-400 mt-1.5 text-sm">Tickets submitted by signed-in client accounts from the portal Support page.</p>
      </div>

      <div className="mb-5 flex gap-2">
        {["all", "unread", "read"].map((f) => (
          <button key={f} data-testid={`tickets-filter-${f}`} onClick={() => setFilter(f)}
            className={"px-3.5 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors border " + (filter === f ? "bg-amber-500/15 text-amber-300 border-amber-500/30" : "text-gray-400 border-gray-800 hover:text-white")}>
            {f}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-800 bg-[#0E131F] py-20 text-center" data-testid="tickets-empty">
          <LifeBuoy className="mx-auto h-10 w-10 text-gray-600" />
          <p className="mt-3 text-gray-400">No tickets here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((t, i) => (
            <motion.div
              key={t.id}
              data-testid={`ticket-${t.id}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.03, 0.3) }}
              className={"rounded-xl border bg-[#0E131F] p-5 " + (t.read ? "border-gray-800" : "border-amber-500/30")}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {!t.read && <span className="h-2 w-2 rounded-full bg-amber-400 shrink-0" title="Unread" />}
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-300 border border-sky-500/20"><Tag className="h-3 w-3" /> Client</span>
                    <span className="font-semibold text-white truncate">{t.subject}</span>
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap text-sm">
                    <span className="text-gray-300">{t.name}</span>
                    <a href={`mailto:${t.email}`} className="text-amber-400 hover:underline">{t.email}</a>
                    {t.organization && <span className="text-xs text-gray-500 inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{t.organization}</span>}
                  </div>
                  <p className="mt-2 text-sm text-gray-300 whitespace-pre-wrap">{t.message}</p>
                  <p className="mt-2 text-xs text-gray-600 inline-flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDate(t.created_at)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <a data-testid={`ticket-reply-${t.id}`} href={`mailto:${t.email}?subject=Re: ${encodeURIComponent(t.subject || "Your StreamWorship support request")}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25 text-xs font-medium transition-colors"><Reply className="h-3.5 w-3.5" /> Reply</a>
                  <button data-testid={`ticket-toggle-read-${t.id}`} onClick={() => toggleRead(t)} disabled={busy === t.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 text-gray-300 border border-gray-700 hover:text-white text-xs font-medium transition-colors disabled:opacity-50" title={t.read ? "Mark as unread" : "Mark as read"}>
                    {t.read ? <Mail className="h-3.5 w-3.5" /> : <MailOpen className="h-3.5 w-3.5" />}
                    {t.read ? "Unread" : "Read"}
                  </button>
                  <button data-testid={`ticket-delete-${t.id}`} onClick={() => remove(t)} disabled={busy === t.id} className="inline-flex items-center px-2.5 py-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 border border-gray-700 hover:border-red-500/30 transition-colors disabled:opacity-50" title="Delete">
                    {busy === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
